// src/components/admin/sections/AdminPrograms.tsx
import React, { useState, useEffect } from 'react';
import apiElCristo, { Program, ProgramsData } from '../../../service/api_elcristo';

const AdminPrograms: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingProgram, setEditingProgram] = useState<Program | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);

  const [programsData, setProgramsData] = useState<ProgramsData>({
    title: '',
    subtitle: '',
    programs: []
  });

  const [formData, setFormData] = useState<Program>({
    image: '',
    title: '',
    ageRange: '',
    description: '',
    link: '',
    backgroundColor: 'default'
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const data = await apiElCristo.getProgramsData();
      setProgramsData(data);
    } catch (error) {
      console.error('Error loading programs:', error);
      setMessage({ type: 'error', text: 'Error al cargar los programas' });
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setProgramsData(prev => ({ ...prev, [name]: value }));
  };

  const handleOpenModal = (program?: Program, index?: number) => {
    if (program && index !== undefined) {
      setEditingProgram(program);
      setEditingIndex(index);
      setFormData(program);
    } else {
      setEditingProgram(null);
      setEditingIndex(null);
      setFormData({
        image: '',
        title: '',
        ageRange: '',
        description: '',
        link: '',
        backgroundColor: 'default'
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingProgram(null);
    setEditingIndex(null);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSaveProgram = () => {
    if (editingIndex !== null) {
      // Actualizar programa existente
      const updated = [...programsData.programs];
      updated[editingIndex] = formData;
      setProgramsData(prev => ({ ...prev, programs: updated }));
    } else {
      // Agregar nuevo programa
      setProgramsData(prev => ({
        ...prev,
        programs: [...prev.programs, formData]
      }));
    }
    handleCloseModal();
  };

  const handleDeleteProgram = (index: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este programa?')) {
      return;
    }

    setProgramsData(prev => ({
      ...prev,
      programs: prev.programs.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await apiElCristo.updateProgramsData(programsData);
      setMessage({ type: 'success', text: '✅ Programas actualizados correctamente' });
      setTimeout(() => setMessage(null), 3000);
    } catch (error: any) {
      console.error('Error saving programs:', error);
      setMessage({ type: 'error', text: '❌ Error al guardar: ' + error.message });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="elcristo-admin-content">
        <div className="elcristo-loading-spinner">
          <div className="elcristo-spinner"></div>
          <p>Cargando programas...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="elcristo-admin-content">
      <div className="elcristo-content-header">
        <div>
          <h1><i className="fas fa-graduation-cap"></i> Gestión de Programas Educativos</h1>
          <p>Administrar niveles y programas del colegio</p>
        </div>
        <button className="elcristo-btn-primary" onClick={() => handleOpenModal()}>
          <i className="fas fa-plus"></i>
          Nuevo Programa
        </button>
      </div>

      {message && (
        <div className={`elcristo-alert alert-${message.type}`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit}>
        {/* Section Settings */}
        <div className="elcristo-admin-form">
          <div className="elcristo-form-section">
            <h3><i className="fas fa-cog"></i> Configuración de Sección</h3>
            
            <div className="elcristo-form-row">
              <div className="elcristo-form-group">
                <label htmlFor="subtitle">Subtítulo de Sección</label>
                <input
                  type="text"
                  id="subtitle"
                  name="subtitle"
                  value={programsData.subtitle}
                  onChange={handleSectionChange}
                  placeholder="Ej: Nuestros Programas"
                />
              </div>

              <div className="elcristo-form-group">
                <label htmlFor="title">Título de Sección</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={programsData.title}
                  onChange={handleSectionChange}
                  placeholder="Ej: Niveles Educativos"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Programs Grid */}
        <div className="elcristo-programs-section">
          <div className="elcristo-section-header">
            <h3><i className="fas fa-list"></i> Programas ({programsData.programs.length})</h3>
          </div>

          <div className="elcristo-programs-grid">
            {programsData.programs.map((program, index) => (
              <div key={index} className="elcristo-program-card">
                <div className="elcristo-program-icon">
                  <img src={program.image} alt={program.title} />
                </div>
                <div className="elcristo-program-content">
                  <h4>{program.title}</h4>
                  <div className="elcristo-program-meta">
                    <span className="program-age">
                      <i className="fas fa-child"></i>
                      {program.ageRange}
                    </span>
                  </div>
                  <p>{program.description}</p>
                </div>
                <div className="elcristo-program-actions">
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-edit"
                    onClick={() => handleOpenModal(program, index)}
                  >
                    <i className="fas fa-edit"></i>
                  </button>
                  <button
                    type="button"
                    className="elcristo-btn-icon btn-delete"
                    onClick={() => handleDeleteProgram(index)}
                  >
                    <i className="fas fa-trash"></i>
                  </button>
                </div>
              </div>
            ))}

            {programsData.programs.length === 0 && (
              <div className="elcristo-empty-state">
                <i className="fas fa-graduation-cap"></i>
                <p>No hay programas educativos</p>
                <button
                  type="button"
                  className="elcristo-btn-primary"
                  onClick={() => handleOpenModal()}
                >
                  <i className="fas fa-plus"></i>
                  Agregar Primer Programa
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="elcristo-form-actions">
          <button type="button" className="elcristo-btn-secondary" onClick={loadData} disabled={saving}>
            <i className="fas fa-undo"></i>
            Recargar
          </button>
          
          <button type="submit" className="elcristo-btn-primary" disabled={saving}>
            {saving ? (
              <>
                <span className="elcristo-spinner-small"></span>
                Guardando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Guardar Cambios
              </>
            )}
          </button>
        </div>
      </form>

      {/* Modal */}
      {showModal && (
        <div className="elcristo-modal-overlay" onClick={handleCloseModal}>
          <div className="elcristo-modal-content modal-large" onClick={e => e.stopPropagation()}>
            <div className="elcristo-modal-header">
              <h2>
                <i className="fas fa-graduation-cap"></i>
                {editingProgram ? 'Editar Programa' : 'Nuevo Programa'}
              </h2>
              <button className="elcristo-modal-close" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
              </button>
            </div>

            <div className="elcristo-modal-body">
              <div className="elcristo-form-row">
                <div className="elcristo-form-group">
                  <label>Título del Programa *</label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    placeholder="Ej: Pre-Kínder"
                    required
                  />
                </div>

                <div className="elcristo-form-group">
                  <label>Rango de Edad *</label>
                  <input
                    type="text"
                    name="ageRange"
                    value={formData.ageRange}
                    onChange={handleChange}
                    placeholder="Ej: 4-5 años"
                    required
                  />
                </div>
              </div>

              <div className="elcristo-form-row">
                <div className="elcristo-form-group">
                  <label>Color de Fondo *</label>
                  <select
                    name="backgroundColor"
                    value={formData.backgroundColor}
                    onChange={handleChange}
                    required
                  >
                    <option value="default">Default (Blanco)</option>
                    <option value="bg-2">Color 2 (Gris)</option>
                    <option value="bg-4">Color 4 (Azul claro)</option>
                  </select>
                </div>

                <div className="elcristo-form-group">
                  <label>Link</label>
                  <input
                    type="text"
                    name="link"
                    value={formData.link}
                    onChange={handleChange}
                    placeholder="Ej: #programas o URL"
                  />
                </div>
              </div>

              <div className="elcristo-form-group">
                <label>Descripción *</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Descripción del programa educativo"
                  rows={4}
                  required
                />
              </div>

              <div className="elcristo-form-group">
                <label>Imagen URL *</label>
                <input
                  type="text"
                  name="image"
                  value={formData.image}
                  onChange={handleChange}
                  placeholder="/assets/kidsa/assets/img/program/icon-1.png"
                  required
                />
                {formData.image && (
                  <div className="elcristo-icon-preview">
                    <img src={formData.image} alt="Preview" />
                  </div>
                )}
              </div>
            </div>

            <div className="elcristo-modal-footer">
              <button type="button" className="elcristo-btn-secondary" onClick={handleCloseModal}>
                <i className="fas fa-times"></i>
                Cancelar
              </button>
              <button type="button" className="elcristo-btn-primary" onClick={handleSaveProgram}>
                <i className="fas fa-save"></i>
                {editingProgram ? 'Actualizar' : 'Agregar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPrograms;